import React, { useEffect, useState } from 'react';
import { ReptoolModal } from '../../utilities/ReptoolModal.tsx';
import { CollectionReptile, CollectionCareRecordType, CollectionCareRecord } from '../types/CollectionTypes.ts';
import { BasicFetch } from '../../../utils/BasicFetch.ts';
import { Button, Table } from 'react-bootstrap';
import { formatDate } from '../../utilities/FormatDate.ts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSortDown, faSortUp, faTimes } from '@fortawesome/free-solid-svg-icons';
import { driver } from 'driver.js';
import { useRepToolContext } from '../../../RepToolContext.tsx';

const RecordEnumMapping = {
  "feed_dates": "FEED",
  "substrate_dates": "SUBSTRATE",
  "def_dates": "DEFECATION",
  "shed_dates": "SHED",
}

const RecordLabels = {
  "feed_dates": "Feedings",
  "substrate_dates": "Substrate Changes",
  "def_dates": "Defecations",
  "shed_dates": "Sheds",
}


export const CareInfoRow = ({
  onDelete,
  onChange,
  record,
  hasToday,
  readOnly,
  addTypeColumn, // This is a prop used to add a column to the table that displays the type of care record (feed, shed, etc.) and is used in the the Scan and feeding modals
}: {
  onDelete: (recordId: number) => void,
  onChange: (id: number, description: string, date: string) => void,
  record: CollectionCareRecord,
  readOnly?: boolean,
  hasToday?: boolean,
  addTypeColumn?: string,
}) => {
  const [editingDescription, setEditingDescription] = useState<string>(record.description);
  const [viewFullDescription, setViewFullDescription] = useState(false);

  const setUpCalendar = () => {
    if (readOnly) return;
    (window as any).setUpCalendar(
      "",
      () => (date: Date) => {
        onChange(record.id, editingDescription, formatDate(date || new Date()));
        (window as any).closeCalendar();
      },
      new Date(record.date)
    );
  }

  useEffect(() => {
    setEditingDescription(record.description);
  }, [record]);

  const isToday = new Date(record.date).toDateString() === new Date().toDateString();

  const numOfColumns = addTypeColumn ? 4 : 3;
  return <>
    <tr
      style={{
        opacity: (hasToday && isToday) || !hasToday || (viewFullDescription) ? 1 : 0.4,
        borderBottom: !viewFullDescription ? '1px solid' : 'none',
      }}>
      <td>
        {<div onClick={() => !readOnly ? setUpCalendar() : null}>
          {new Date(record.date).toLocaleDateString()}
        </div>}
      </td>
      <td className='text-truncate' onClick={() => setViewFullDescription(!viewFullDescription)}>
        {record.description ? <span className='ml-2'>{record.description}</span> : <span className='text-muted ml-2'>No notes</span>}
      </td>
      {addTypeColumn && <td>{addTypeColumn}</td>}
      <td>
        <Button className='float-right' variant='outline-light' onClick={() => {
          onDelete(record.id);
        }}>
          <FontAwesomeIcon icon={faTimes} />
        </Button>
      </td>
    </tr>
    {viewFullDescription && <tr>
      <td colSpan={numOfColumns} style={{ borderTop: 0 }}>
        <label>Description for {new Date(record.date).toLocaleDateString()}</label>
        {!readOnly ? <textarea
          className='form-control'
          value={editingDescription}
          readOnly={readOnly}
          onChange={(e) => {
            setEditingDescription(e.target.value);
          }}
        /> : editingDescription}
        {!readOnly ? <Button className='mt-2 w-100' variant='outline-light' onClick={() => {
          onChange(record.id, editingDescription, record.date);
          setViewFullDescription(false);
        }}>Save</Button> : <Button className='mt-2 w-100' variant='outline-light' onClick={() => {
          setViewFullDescription(false);
        }
        }>Close</Button>}
      </td>
    </tr>}
  </>
}





export const ReptileCareInfoModal = ({
  collectionReptile,
  careType,
  setReptile,
  closeModal,
  readOnly,
}: {
  collectionReptile: CollectionReptile,
  careType: CollectionCareRecordType,
  setReptile: (reptile: CollectionReptile) => void,
  closeModal: () => void,
  readOnly?: boolean,
}) => {
  const [sortMethod, setSortMethod] = useState(localStorage.getItem('reptileDateSortMethod') || 'desc');
  const [careRecords, setCareRecords] = useState<CollectionCareRecord[]>([]);
  const [dateRange, setDateRange] = useState<{ min?: Date, max?: Date }>({
    min: undefined,
    max: undefined,
  });
  const { appendControls } = useRepToolContext();


  useEffect(() => {
    if (dateRange.min || dateRange.max) {
      setCareRecords([...collectionReptile[careType]].filter(record => {
        if (dateRange.min && dateRange.max) {
          return new Date(record.date) >= dateRange.min && new Date(record.date) <= dateRange.max;
        } else if (dateRange.min) {
          return new Date(record.date) >= dateRange.min;
        } else if (dateRange.max) {
          return new Date(record.date) <= dateRange.max;
        }
        return true;
      }).sort((a, b) => sortMethod === 'desc' ? new Date(b.date).getTime() - new Date(a.date).getTime() : new Date(a.date).getTime() - new Date(b.date).getTime()));
    } else {
      setCareRecords([...collectionReptile[careType]].sort((a, b) => sortMethod === 'desc' ? new Date(b.date).getTime() - new Date(a.date).getTime() : new Date(a.date).getTime() - new Date(b.date).getTime()));
    }
    localStorage.setItem('reptileDateSortMethod', sortMethod);
  }, [sortMethod, collectionReptile, dateRange]);

  const onChange = (id: number, description: string, date: string) => {
    const data = new FormData();
    data.append("date", formatDate(date).toUTCString());
    data.append("description", description);

    BasicFetch({
      url: `${(window as any).serverLocation}collection_reptile/${collectionReptile.id}/care_info/${id}/edit`,
      data,
      method: 'POST',
      contentType: 'multipart/form-data',
      onSuccess: (data: { reptile: CollectionReptile }) => {
        setReptile({ ...data.reptile });
      },
      onFail: () => {
        (window as any).alert("There was an error saving changes, please try again later");
      },
    })

  }

  const deleteRecord = (recordId: number) => {
    BasicFetch({
      url: `${(window as any).serverLocation}/collection_reptile/${collectionReptile.id}/care_info/${recordId}/delete`,
      method: 'DELETE',
      onSuccess: (data) => {
        setReptile(data.reptile);
      }, onFail: () => {
        window.alert("There was a problem deleting the care record. Please try again later.");
      }
    })
  }

  const addRecord = () => {
    BasicFetch({
      url: `${(window as any).serverLocation}/collection_reptile/${collectionReptile.id}/care_info/${RecordEnumMapping[careType]}/create`,
      extra: {
        return_record: true,
      },
      method: 'POST',
      onSuccess: ({ record }) => {
        setReptile({ ...collectionReptile, [careType]: [...collectionReptile[careType], record] });
      }, onFail: () => {
        window.alert("There was a problem saving your changes. Please try again later.");
      }
    })
  }
  const setUpCalendar = (key: string, title: string, value: Date) => {
    if (readOnly) return;
    (window as any).setUpCalendar(
      title,
      () => (date: Date) => {
        setDateRange(prev => ({ ...prev, [key]: date }));
        (window as any).closeCalendar();
      },
      value
    );
  }
  useEffect(() => {
    appendControls(
      !readOnly ? <div className='d-flex mx-auto'>
        <Button id='add-record-button' variant='outline-light' className='mx-auto' onClick={addRecord}>Add Record</Button>
      </div> : null, `${RecordLabels[careType]} Records`
    );
  }, []);
  const driverObj = driver({
    steps: [
      { popover: { title: `${RecordLabels[careType]} records`, description: `This is the history of all ${RecordLabels[careType]} of your animal.` } },
      { element: '#add-record-button', popover: { title: '', description: "Click here to add a new record." } },
      { element: '#reptile-care-info-modal #records', popover: { title: '', description: "Once a record has been added you can edit the date and add notes to the record. To add a note simply click the note text then fill out the field that appears and click save." } },
    ]
  });
  
  const hasToday = careRecords.some(record => new Date(record.date).toDateString() === new Date().toDateString());

  return (
    <ReptoolModal
      id="reptile-care-info-modal"
      title={RecordLabels[careType]}
      closeModal={closeModal}
      showTutorial={() => driverObj.drive()}
      body={(
        <div className='mb-5 pb-5'>
          <Table id='records' variant='dark' style={{
            tableLayout: 'fixed',
          }}>
            <thead>
              <tr>
                <th>
                  <div className='d-flex'>
                    <span>Date</span>
                    <FontAwesomeIcon className='ml-2 my-auto' icon={sortMethod === 'desc' ? faSortDown : faSortUp} onClick={() => setSortMethod(sortMethod === 'desc' ? 'asc' : 'desc')} />
                  </div>
                </th>
                <th colSpan={2}>
                  <div className='d-flex justify-content-between'>
                    <div>Notes</div>

                    <div>
                      <small
                        className={dateRange.min ? 'text-light' : 'text-muted'}
                        onClick={() => setUpCalendar('min', 'Select start date', dateRange.min ? new Date(dateRange.min) : new Date())}
                      >
                        {dateRange.min ? formatDate(dateRange.min).toLocaleDateString() : 'mm/dd/yyyy'}
                      </small>
                      <span className={`mx-2 text-${dateRange.min || dateRange.max ? 'light' : 'muted'}`}>-</span>
                      <small
                        className={dateRange.max ? 'text-light' : 'text-muted'}
                        onClick={() => setUpCalendar('max', 'Select end date', dateRange.max ? new Date(dateRange.max) : new Date())}
                      >
                        {dateRange.max ? formatDate(dateRange.max).toLocaleDateString() : 'mm/dd/yyyy'}
                      </small>
                    </div>

                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {careRecords.map(record => (
                <CareInfoRow
                  key={record.id}
                  record={record}
                  hasToday={hasToday}
                  onDelete={deleteRecord}
                  onChange={onChange}
                  readOnly={readOnly}
                />
              ))}
            </tbody>
          </Table>

        </div>
      )} />
  )
}